<script>
    import { onMount } from "svelte";

    import {
        MultiSearchSelect,
        NotificationModal,
        NumericInput,
        DateInput,
    } from "../../../Components/Common/index.svelte";

    import { accountCalendarRepeatIntervalType } from "../../../Utils/utilEnums.svelte";
    import { getYYYYMMDD } from "../../../Utils/utilFunctions.svelte";
    import { checkRecurrenceValidation } from "../helpers/calendarHelper.svelte";

    export let isModalOpen = false,
        startDate,
        eventObj = {};

    let backUpRecurrence = {};

    $: noticeText = checkRecurrenceValidation(eventObj).text;

    $: isConfirmDisabled = checkRecurrenceValidation(eventObj).isDisabled;

    let DEFAULT_EVENT_OBJ = {};

    let isInitRepeatDetails = false;

    let repeatMonth = [];

    let noticeText = "";

    const DEFAULT_REPEAT_OBJ = {
        repeatDaysOfWeek: [],
        repeatDaysOfMonth: [],
        repeatDaysOfYear: [],
        repeatInterval: Number(0),
        repeatIntervalType: Number(0),
        repeatEnd: null,
        repeatEndType: Number(0), // 0 - Never; 1 - On
        selectedRepeatYearDay: null, // for repeatDaysOfYear
    };

    const repeatKeys = [
        "repeatInterval",
        "repeatIntervalType",
        "repeatDaysOfWeek",
        "repeatDaysOfMonth",
        "repeatDaysOfYear",
        "repeatEnd",
        "selectedRepeatYearDay",
    ];

    const init = () => {
        backUpRecurrence = {
            isRepeating: eventObj.repeatIntervalType > 0,
        }

        repeatKeys.forEach((key) => backUpRecurrence[key] = eventObj[key]);
    }

    const repeatWeek = [
        { text: "S", day: 0 },
        { text: "M", day: 1 },
        { text: "T", day: 2 },
        { text: "W", day: 3 },
        { text: "T", day: 4 },
        { text: "F", day: 5 },
        { text: "S", day: 6 },
    ];

    onMount(() => {
        _initEventObj();
    });

    const cancelEdit = () => {
        eventObj = {
            ...eventObj,
            ...backUpRecurrence,
        };

        backUpRecurrence = {};

        closeModal();
    }

    const closeModal = () => {
        isModalOpen = false;
        isInitRepeatDetails = false;
        repeatMonth = [];
        noticeText = "";
        backUpRecurrence = {};
        startDate = undefined;
    };

    const confirmEdit = () => {
        eventObj = _syncRepeatObj(eventObj);

        closeModal();
    }

    const handleRepeatIntervalTypeChange = () => {
        switch (eventObj.repeatIntervalType) {
            case 1:
            default:
                _resetRepeatOnWeek();
                _resetRepeatOnMonth();
                _resetRepeatOnYear();
                break;
            case 2:
                if (startDate) 
                    eventObj.repeatDaysOfWeek = [new Date(startDate).getDay()];
                _resetRepeatOnMonth();
                _resetRepeatOnYear();
                break;
            case 3:
                _generateRepeatMonthOptions();
                if (startDate)
                    eventObj.repeatDaysOfMonth = [new Date(startDate).getDate()];
                _resetRepeatOnWeek();
                _resetRepeatOnYear();
                break;
            case 4:
                if (startDate) 
                    eventObj.selectedRepeatYearDay = [getYYYYMMDD(startDate)];
                _resetRepeatOnWeek();
                _resetRepeatOnMonth();
                break;
        }
    };

    const handleSelectRepeatWeek = (day) => {
        if (!eventObj.hasOwnProperty("repeatDaysOfWeek"))
            eventObj.repeatDaysOfWeek = [day];
        else if (eventObj.repeatDaysOfWeek.includes(day))
            eventObj.repeatDaysOfWeek = eventObj.repeatDaysOfWeek.filter(
                (d) => d !== day,
            );
        else eventObj.repeatDaysOfWeek = [...eventObj.repeatDaysOfWeek, day];
    };

    // HELPER FUNCTIONS
    const _initEventObj = async () => {
        if (
            !eventObj.hasOwnProperty("repeatInterval") ||
            eventObj.repeatInterval == 0
        )
            eventObj.repeatInterval = Number(1);

        if (
            !eventObj.hasOwnProperty("repeatIntervalType") ||
            !eventObj.repeatIntervalType
        )
            eventObj.repeatIntervalType = Number(1);

        isInitRepeatDetails = true;

        if (eventObj.repeatIntervalType == 2) await _initRepeatWeek();
        else if (eventObj.repeatIntervalType == 3) await _initRepeatMonth();
        else if (eventObj.repeatIntervalType == 4) await _initRepeatYear();

        isInitRepeatDetails = false;

        if (eventObj.repeatEnd) eventObj.repeatEndType = Number(1);
        else eventObj.repeatEndType = Number(0);

        DEFAULT_EVENT_OBJ = { ...eventObj };
    };

    const _initRepeatWeek = () => {
        const { repeatDaysOfWeek } = eventObj;

        if (!repeatDaysOfWeek) eventObj.repeatDaysOfWeek = [];
    };

    const _initRepeatMonth = () => {
        _generateRepeatMonthOptions();

        const { repeatDaysOfMonth } = eventObj;

        if (!repeatDaysOfMonth) eventObj.repeatDaysOfMonth = [];
    };

    const _initRepeatYear = () => {
        const { repeatDaysOfYear } = eventObj;

        if (!repeatDaysOfYear) {
            eventObj.repeatDaysOfYear = [];
            return;
        }

        const daysFromStart = Number(repeatDaysOfYear);

        const currentYear = new Date().getFullYear();
        const startOfYear = new Date(currentYear, 0, 1);

        startOfYear.setDate(startOfYear.getDate() + daysFromStart - 1);

        const year = startOfYear.getFullYear();
        const month = String(startOfYear.getMonth() + 1).padStart(2, "0");
        const day = String(startOfYear.getDate()).padStart(2, "0");

        eventObj.selectedRepeatYearDay = `${year}-${month}-${day}`;

        return eventObj;
    };

    const _resetRepeatOnWeek = () => (eventObj.repeatDaysOfWeek = []);

    const _resetRepeatOnMonth = () => (eventObj.repeatDaysOfMonth = []);

    const _resetRepeatOnYear = () => (eventObj.selectedRepeatYearDay = null);

    const _syncRepeatObj = (obj = {}) => {
        if (!obj.repeatDaysOfWeek?.length) obj.repeatDaysOfWeek = [];

        if (!obj.repeatDaysOfMonth?.length) obj.repeatDaysOfMonth = [];

        if (obj.selectedRepeatYearDay)
            obj.repeatDaysOfYear = [_getDayOfYear(obj.selectedRepeatYearDay)];
        else obj.repeatDaysOfYear = [];

        if (
            obj.repeatEndType == 0 ||
            !obj.repeatEnd
        )
            obj.repeatEnd = null;

        return obj;
    };

    const _generateRepeatMonthOptions = () => {
        if (repeatMonth.length) return;

        for (let i = 0; i < 31; i++) {
            repeatMonth = [
                ...repeatMonth,
                {
                    id: i + 1,
                    type: i + 1,
                },
            ];
        }
    };

    const _getDayOfYear = (dateStr) => {
        const date = new Date(dateStr);

        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const timeDiff = date - startOfYear;

        return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
    };
</script>

<NotificationModal
    {isModalOpen}
    title="Custom recurrence"
    cancelText="Cancel"
    closeModal={cancelEdit}
    closeModalFunction={cancelEdit}
    submitText="Done"
    confirmAction={confirmEdit}
    {isConfirmDisabled}
>
    <div class="flex flex-col gap-y-4" use:init>
        <div class="flex items-center w-full gap-x-4">
            <p class="title">Repeat every</p>

            <NumericInput bind:value={eventObj.repeatInterval} />

            <select
                class="select select-sm select-bordered"
                bind:value={eventObj.repeatIntervalType}
                on:change={handleRepeatIntervalTypeChange}
            >
                {#each accountCalendarRepeatIntervalType.filter((it) => it.id) as intervalType}
                    <option value={intervalType.id}>
                        {intervalType.type}{eventObj.repeatInterval > 1
                            ? "s"
                            : ""}
                    </option>
                {/each}
            </select>
        </div>

        {#if !isInitRepeatDetails}
            {#if eventObj.repeatIntervalType == Number(2)}
                <div class="flex flex-col w-full gap-y-2">
                    <p>Repeat on</p>

                    <div class="flex flex-1 w-full gap-x-2">
                        {#each repeatWeek as rw}
                            <div
                                class={`flex items-center justify-center w-6 h-6 rounded-full cursor-pointer text-sm ${eventObj.repeatDaysOfWeek?.includes(rw.day) ? "bg-secondary text-[#fff]" : "bg-gray-200 text-secondary"}`}
                                on:click={() => handleSelectRepeatWeek(rw.day)}
                            >
                                <p class="repeatWeekTitle">{rw.text}</p>
                            </div>
                        {/each}
                    </div>
                </div>
            {:else if eventObj.repeatIntervalType == Number(3)}
                <div class="flex flex-col w-full gap-y-2">
                    <p>Repeat on</p>

                    <MultiSearchSelect
                        bind:searchResult={eventObj.repeatDaysOfMonth}
                        optionsArr={repeatMonth}
                        inputID="repeatMonthInput"
                        inputClass="bg-[#fff]"
                        onChangeFunc={() =>
                            (eventObj.repeatDaysOfMonth =
                                eventObj.repeatDaysOfMonth)}
                    />
                </div>
            {:else if eventObj.repeatIntervalType == Number(4)}
                <div class="flex flex-col w-full gap-y-2">
                    <p>Repeat on</p>

                    <DateInput
                        bind:value={eventObj.selectedRepeatYearDay}
                        showYearDropdown={false}
                        wrapperClass="w-24 input input-sm input-bordered"
                    />
                </div>
            {/if}
        {/if}

        <div class="flex flex-col w-full gap-y-2">
            <p>Ends</p>
            <div class="flex gap-x-2 title">
                <input
                    type="radio"
                    id="never"
                    name="radio-3"
                    class="radio radio-secondary"
                    checked={!eventObj.hasOwnProperty("repeatEndType") ||
                        eventObj.repeatEndType == 0}
                    on:change={() => (eventObj.repeatEndType = 0)}
                />
                <label for="never">Never</label>
            </div>

            <div class="flex gap-x-4">
                <div class="flex gap-x-2 items-center title">
                    <input
                        type="radio"
                        id="end"
                        name="radio-3"
                        class="radio radio-secondary"
                        checked={eventObj.repeatEndType == 1}
                        on:change={() => (eventObj.repeatEndType = 1)}
                        disabled={!eventObj.hasOwnProperty(
                            "repeatIntervalType",
                        )}
                    />
                    <label for="end">On</label>
                </div>

                <DateInput
                    bind:value={eventObj.repeatEnd}
                    isDisable={!eventObj.hasOwnProperty("repeatEndType") ||
                        eventObj.repeatEndType != 1}
                    wrapperClass="w-36 input input-sm input-bordered"
                />
            </div>
        </div>

        {#if noticeText}
            <p class="text-error">{noticeText}</p>
        {/if}
    </div>
</NotificationModal>

<style>
    .title {
        width: 120px;
    }

    .repeatWeekTitle {
        padding-top: 5px;
    }
</style>
